import { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { CUENTAS_ER } from "../../constants/clasificacionContable";
import { useAuthContext } from "../../context/auth";
import { crearMovimiento } from "../../utils/flujoCaja";
import { actualizarMatch, confirmarMovimiento } from "../../utils/movimientosBancarios";
import { crearProveedor } from "../../utils/proveedores";
import { client } from "../../utils/supabase";
import ProveedorSelector from "../VincularMovimientoModal/ProveedorSelector";

// ─── Helpers ─────────────────────────────────────────────────
const subirArchivoFactura = async (file) => {
  const ext = file.name.split(".").pop();
  const path = `externas/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
  const { error } = await client.storage.from("facturas").upload(path, file);
  if (error) throw error;
  const { data } = client.storage.from("facturas").getPublicUrl(path);
  return data.publicUrl;
};

// Date del primer día del mes de `fechaStr` (YYYY-MM-DD). Parseo manual evita el bug
// de TZ que aparece con `new Date("YYYY-MM-DD")` en husos negativos (Chile/Perú).
const defaultPeriodoMes = (fechaStr) => {
  if (!fechaStr) {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }
  const [y, m] = fechaStr.split("-").map(Number);
  return new Date(y, m - 1, 1);
};

// Serializa la Date del periodo a "YYYY-MM-01" sin pasar por toISOString — que en
// husos negativos retorna el último día del mes anterior.
const periodoMesToString = (date) => {
  if (!date) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
};

const MESES_LABELS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

// ─── Hook con toda la lógica de form ────────────────────────
// Centraliza state + validación + submit. Permite que el padre (VincularMov)
// dispare submit desde su propio botón sin duplicar código.
export const useFacturaExternaForm = ({
  pais,
  moneda: monedaInitial,
  monto: montoInitial,
  proveedorId: proveedorIdLocked,
  proveedores,
  movimiento,
  onProveedorCreado,
  onFacturaCreada,
  onSuccess,
  onClose,
}) => {
  const { id: usuarioId } = useAuthContext();
  const isModoBancario = !!movimiento;

  const [proveedorId, setProveedorId] = useState(proveedorIdLocked || null);
  const [modoNuevoProv, setModoNuevoProv] = useState(false);
  const [nuevoProvNombre, setNuevoProvNombre] = useState("");
  const [nuevoProvRut, setNuevoProvRut] = useState("");

  const [extFecha, setExtFecha] = useState(movimiento?.fecha || new Date().toISOString().split("T")[0]);
  const [extPeriodoMes, setExtPeriodoMes] = useState(() =>
    defaultPeriodoMes(movimiento?.fecha || new Date().toISOString().split("T")[0])
  );
  const [extNumeroDoc, setExtNumeroDoc] = useState("");
  const [extConcepto, setExtConcepto] = useState("");
  const [extCuentaER, setExtCuentaER] = useState("");
  const [extArchivo, setExtArchivo] = useState(null);
  const [extMonto, setExtMonto] = useState(montoInitial != null ? String(montoInitial) : "");
  const [extMoneda, setExtMoneda] = useState(monedaInitial || "USD");

  const [loading, setLoading] = useState(false);

  const proveedorIdFinal = proveedorIdLocked || proveedorId;
  const proveedorLockedNombre = useMemo(
    () => (proveedorIdLocked ? proveedores.find((p) => p.id === proveedorIdLocked)?.nombre : null),
    [proveedorIdLocked, proveedores]
  );

  // El campo "Período" solo aplica a proveedores recurrentes (suscripciones, servicios mensuales).
  // Para compras puntuales (ej: un pasaje LATAM) no tiene sentido — se oculta y periodo_mes va NULL.
  // Proveedores nuevos creados desde este form no son recurrentes hasta editarlos, así que false.
  const esRecurrente = useMemo(
    () => !!proveedores.find((p) => p.id === proveedorIdFinal)?.es_recurrente,
    [proveedores, proveedorIdFinal]
  );

  const montoFinal = isModoBancario ? Number(movimiento.monto || 0) : Number(extMonto || 0);
  const monedaFinal = isModoBancario ? monedaInitial : extMoneda;

  // Auto-preselect por descripción cruda del banco — solo modo bancario y solo si no hay
  // proveedor ya seleccionado/lockeado.
  useEffect(() => {
    if (!isModoBancario || proveedorIdLocked || proveedorId || proveedores.length === 0) return;
    const desc = (movimiento?.descripcion_raw || "").toLowerCase();
    if (!desc) return;
    const match = proveedores
      .filter((p) => p.es_extranjero)
      .find((p) => {
        const candidatos = [p.nombre, p.alias].filter(Boolean).map((s) => s.toLowerCase());
        return candidatos.some((c) => desc.includes(c));
      });
    if (match) {
      setProveedorId(match.id);
      if (match.cuenta_er_default && !extCuentaER) setExtCuentaER(match.cuenta_er_default);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModoBancario, proveedores, movimiento?.descripcion_raw]);

  const isValid =
    !loading &&
    !!(proveedorIdFinal || (modoNuevoProv && nuevoProvNombre.trim())) &&
    !!extFecha &&
    !!extConcepto.trim() &&
    !!extCuentaER &&
    !!extArchivo &&
    (isModoBancario || montoFinal > 0);

  const handleSubmit = async () => {
    if (!proveedorIdFinal && !modoNuevoProv) return toast.error("Seleccioná un proveedor o creá uno nuevo");
    if (modoNuevoProv && !nuevoProvNombre.trim()) return toast.error("Ingresá el nombre del nuevo proveedor");
    if (!extFecha) return toast.error("Ingresá la fecha del documento");
    if (!extConcepto.trim()) return toast.error("Ingresá el concepto");
    if (!extCuentaER) return toast.error("Seleccioná la cuenta del Estado de Resultados");
    if (!extArchivo) return toast.error("Subí el documento (PDF/imagen)");
    if (!isModoBancario && montoFinal <= 0) return toast.error("Ingresá el monto");
    setLoading(true);
    try {
      let provId = proveedorIdFinal;
      let provNombre = proveedores.find((p) => p.id === provId)?.nombre || proveedorLockedNombre || "";
      if (modoNuevoProv) {
        const nuevo = await crearProveedor({
          pais,
          nombre: nuevoProvNombre.trim(),
          rut_ruc: nuevoProvRut.trim() || null,
          es_extranjero: true,
          habilitado: true,
          cuenta_er_default: extCuentaER,
          moneda_recurrente: monedaFinal,
        });
        if (!nuevo?.id) throw new Error("No se pudo crear el proveedor");
        provId = nuevo.id;
        provNombre = nuevo.nombre;
        onProveedorCreado?.(nuevo);
      }

      const archivoUrl = await subirArchivoFactura(extArchivo);

      const { data: fe, error: feErr } = await client
        .from("facturas_externas")
        .insert({
          pais,
          proveedor_id: Number(provId),
          proveedor_nombre: provNombre,
          concepto: extConcepto.trim(),
          numero_documento: extNumeroDoc.trim() || null,
          fecha_emision: extFecha,
          monto_neto: montoFinal,
          monto_impuesto: 0,
          monto_total: montoFinal,
          moneda: monedaFinal,
          cuenta_er: extCuentaER,
          archivo_url: archivoUrl,
          periodo_mes: esRecurrente ? periodoMesToString(extPeriodoMes) : null,
          estado: isModoBancario ? "pagada" : "pendiente",
          movimiento_bancario_id: isModoBancario ? movimiento.id : null,
          registrado_por: usuarioId || null,
        })
        .select()
        .single();
      if (feErr) throw feErr;

      if (isModoBancario) {
        // Converger persistencia con "Vincular existente": fila puente en MBF. Sin esto, al
        // desvincular cae a Sección B (handleDesvincularCaja) que no libera la factura → huérfano.
        const { error: mbfError } = await client.from("movimiento_bancario_facturas").insert({
          movimiento_bancario_id: movimiento.id,
          factura_tipo: "factura_externa",
          factura_id: fe.id,
          monto: montoFinal,
        });
        if (mbfError) {
          // Rollback: borrar la factura recién creada para no dejar huérfana (no hay transacción).
          await client.from("facturas_externas").delete().eq("id", fe.id);
          throw mbfError;
        }

        await actualizarMatch(movimiento.id, {
          match_entidad_tipo: "proveedor",
          match_entidad_id: Number(provId),
          match_entidad_nombre: provNombre,
          match_factura_tipo: "factura_externa",
          match_factura_id: fe.id,
          match_confianza: 1,
          match_razon: "factura_externa_manual",
        });

        const nuevoCaja = await crearMovimiento({
          pais,
          tipo: "pago",
          fecha: movimiento.fecha,
          monto: montoFinal,
          monto_neto: montoFinal,
          monto_impuesto: 0,
          moneda: monedaFinal,
          estado: "confirmado",
          descripcion: `${provNombre} — ${extConcepto.trim()}`,
          entidad_tipo: "proveedor",
          entidad_id: Number(provId),
          entidad_nombre: provNombre,
          categoria_pago: "pago_proveedor",
          origen: "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        });
        if (!nuevoCaja?.id) throw new Error("No se pudo crear el movimiento en caja");

        await confirmarMovimiento(movimiento.id, {
          movimiento_caja_id: nuevoCaja.id,
          procesado_por: usuarioId || null,
        });

        toast.success("Factura externa registrada y movimiento confirmado");
        onSuccess?.({
          id: movimiento.id,
          estado: "procesado",
          movimiento_caja_id: nuevoCaja.id,
          movimiento_caja: { categoria_pago: "pago_proveedor" },
          match_factura_tipo: "factura_externa",
          match_entidad_nombre: provNombre,
          factura_externa: [{ numero_documento: extNumeroDoc.trim() || null }],
          // Update optimista del Set de dedup en FlujoCajaV2 (mismo flag que "Vincular existente").
          _externasVinculadas: [fe.id],
        });
      } else {
        toast.success("Factura externa registrada");
        onFacturaCreada?.(fe);
      }
      onClose?.();
    } catch (err) {
      toast.error("Error al registrar: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  return {
    // state
    proveedorId,
    setProveedorId,
    modoNuevoProv,
    setModoNuevoProv,
    nuevoProvNombre,
    setNuevoProvNombre,
    nuevoProvRut,
    setNuevoProvRut,
    extFecha,
    setExtFecha,
    extPeriodoMes,
    setExtPeriodoMes,
    extNumeroDoc,
    setExtNumeroDoc,
    extConcepto,
    setExtConcepto,
    extCuentaER,
    setExtCuentaER,
    extArchivo,
    setExtArchivo,
    extMonto,
    setExtMonto,
    extMoneda,
    setExtMoneda,
    // computed
    isModoBancario,
    proveedorIdLocked,
    proveedorLockedNombre,
    monedaFinal,
    esRecurrente,
    isValid,
    loading,
    // actions
    handleSubmit,
  };
};

// ─── Form (UI puro, sin Modal wrapper) ───────────────────────
export const FacturaExternaForm = ({ form, pais, proveedores }) => {
  const monedaLocal = pais === "Chile" ? "CLP" : "PEN";

  return (
    <>
      {form.proveedorIdLocked ? (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Proveedor</div>
          <div
            style={{
              padding: "6px 10px",
              border: "1px solid #dee2e6",
              borderRadius: 4,
              background: "#f8f9fa",
              fontSize: "0.85rem",
            }}
          >
            {form.proveedorLockedNombre}
          </div>
        </div>
      ) : (
        <ProveedorSelector
          value={form.proveedorId}
          onChange={(p) => {
            form.setProveedorId(p.id);
            if (p.cuenta_er_default && !form.extCuentaER) form.setExtCuentaER(p.cuenta_er_default);
          }}
          filter={(p) => p.es_extranjero}
          proveedores={proveedores}
          onCrearNuevo={() => form.setModoNuevoProv(true)}
          modoNuevo={form.modoNuevoProv}
          onModoNuevoChange={form.setModoNuevoProv}
          nuevoNombre={form.nuevoProvNombre}
          onNuevoNombreChange={form.setNuevoProvNombre}
          nuevoRut={form.nuevoProvRut}
          onNuevoRutChange={form.setNuevoProvRut}
          hintCreacion={`Se marca como extranjero (es_extranjero=true) y se registra con moneda ${form.monedaFinal}.`}
        />
      )}

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Fecha documento</div>
        <input
          type="date"
          className="form-control form-control-sm"
          value={form.extFecha}
          onChange={(e) => form.setExtFecha(e.target.value)}
          style={{ fontSize: "0.82rem" }}
        />
      </div>

      {form.esRecurrente && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Período</div>
          <div style={{ display: "flex", gap: 8 }}>
            <select
              className="form-select form-select-sm"
              value={form.extPeriodoMes.getMonth()}
              onChange={(e) =>
                form.setExtPeriodoMes(new Date(form.extPeriodoMes.getFullYear(), Number(e.target.value), 1))
              }
              style={{ fontSize: "0.82rem", flex: 1 }}
            >
              {MESES_LABELS.map((label, i) => (
                <option key={i} value={i}>
                  {label}
                </option>
              ))}
            </select>
            <select
              className="form-select form-select-sm"
              value={form.extPeriodoMes.getFullYear()}
              onChange={(e) =>
                form.setExtPeriodoMes(new Date(Number(e.target.value), form.extPeriodoMes.getMonth(), 1))
              }
              style={{ fontSize: "0.82rem", width: 100 }}
            >
              <option value={2025}>2025</option>
              <option value={2026}>2026</option>
              <option value={2027}>2027</option>
            </select>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>N° documento (opcional)</div>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Ej: 3YK4QC1M-0005"
          value={form.extNumeroDoc}
          onChange={(e) => form.setExtNumeroDoc(e.target.value)}
          style={{ fontSize: "0.82rem" }}
        />
      </div>

      {!form.isModoBancario && (
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <div style={{ flex: 2 }}>
            <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Monto</div>
            <input
              type="text"
              inputMode="decimal"
              className="form-control form-control-sm"
              placeholder="0.00"
              value={form.extMonto}
              onChange={(e) => {
                const normalized = e.target.value.replace(",", ".");
                if (normalized === "" || /^\d*\.?\d*$/.test(normalized)) {
                  form.setExtMonto(normalized);
                }
              }}
              style={{ fontSize: "0.82rem" }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Moneda</div>
            <select
              className="form-select form-select-sm"
              value={form.extMoneda}
              onChange={(e) => form.setExtMoneda(e.target.value)}
              style={{ fontSize: "0.82rem" }}
            >
              <option value="USD">USD</option>
              <option value={monedaLocal}>{monedaLocal}</option>
            </select>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Concepto</div>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Ej: Team plan - Standard Apr 18–May 18, 2026"
          value={form.extConcepto}
          onChange={(e) => form.setExtConcepto(e.target.value)}
          style={{ fontSize: "0.82rem" }}
        />
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Cuenta Estado de Resultados</div>
        <select
          className="form-select form-select-sm"
          value={form.extCuentaER}
          onChange={(e) => form.setExtCuentaER(e.target.value)}
          style={{ fontSize: "0.82rem" }}
        >
          <option value="">Seleccionar cuenta…</option>
          {["Costos Fijos", "Costos Variables"].map((grupo) => (
            <optgroup key={grupo} label={grupo}>
              {CUENTAS_ER.filter((c) => c.grupo === grupo).map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Documento (PDF o imagen)</div>
        <input
          type="file"
          className="form-control form-control-sm"
          accept="application/pdf,image/*"
          onChange={(e) => form.setExtArchivo(e.target.files?.[0] || null)}
          style={{ fontSize: "0.82rem" }}
        />
        {form.extArchivo && (
          <div style={{ fontSize: "0.72rem", color: "#27ae60", marginTop: 4 }}>✓ {form.extArchivo.name}</div>
        )}
      </div>
    </>
  );
};

// ─── Modal wrapper (full standalone) ─────────────────────────
const FacturaExternaModal = (props) => {
  const form = useFacturaExternaForm(props);
  if (!props.show) return null;

  return (
    <Modal show={props.show} onHide={props.onClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>Cargar factura externa</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FacturaExternaForm form={form} pais={props.pais} proveedores={props.proveedores} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={props.onClose}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={form.handleSubmit} disabled={!form.isValid}>
          {form.loading ? "Registrando..." : "Confirmar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FacturaExternaModal;
